import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { SystemHealthPanel } from '../components/AdminOverlay';
import {
  Coins, FileText, CheckCircle2, Clock, Wifi, PenSquare,
  Instagram, Youtube, Twitter, Facebook, MessageCircle, Send, Smartphone, Mail,
  ArrowRight, TrendingUp
} from 'lucide-react';

const CHANNEL_ICONS = {
  instagram: Instagram, youtube: Youtube, twitter: Twitter, facebook: Facebook,
  whatsapp: MessageCircle, telegram: Send, sms: Smartphone, email: Mail
};

const CHANNEL_COLORS = {
  instagram: '#E1306C', youtube: '#FF0033', twitter: '#A8B3BD', facebook: '#1877F2',
  whatsapp: '#22C55E', telegram: '#2AABEE', sms: '#84CC16', email: '#F59E0B'
};

export default function Dashboard() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/dashboard')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--primary))]"></div>
      </div>
    );
  }

  const stats = [
    { label: t('dashboard.credits'), value: data?.credits || 0, icon: Coins, color: 'text-[hsl(var(--primary))]', bgColor: 'bg-[hsl(var(--primary))]/10' },
    { label: t('dashboard.totalPosts'), value: data?.total_posts || 0, icon: FileText, color: 'text-[hsl(var(--accent))]', bgColor: 'bg-[hsl(var(--accent))]/10' },
    { label: t('dashboard.published'), value: data?.published_count || 0, icon: CheckCircle2, color: 'text-emerald-400', bgColor: 'bg-emerald-500/10' },
    { label: t('dashboard.scheduled'), value: data?.scheduled_count || 0, icon: Clock, color: 'text-sky-400', bgColor: 'bg-sky-500/10' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          {t('dashboard.title')}
        </h1>
        <Button onClick={() => navigate('/studio')} className="gold-sheen bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" data-testid="dashboard-quick-create">
          <PenSquare size={16} className="mr-2" />
          {t('dashboard.quickCreate')}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <Card key={i} className="card-sheen rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <stat.icon size={18} className={stat.color} />
                </div>
                <div>
                  <p className="text-2xl font-semibold tabular-nums" data-testid={`dashboard-stat-${i}`}>{stat.value}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Admin: System Health */}
      <SystemHealthPanel />

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Connected Channels */}
        <Card className="md:col-span-4 card-sheen rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Wifi size={16} className="text-[hsl(var(--accent))]" />
              {t('dashboard.channels')} ({data?.connected_channels || 0}/8)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(data?.channel_status || {}).map(([ch, status]) => {
              const Icon = CHANNEL_ICONS[ch] || Wifi;
              return (
                <div key={ch} className={`flex items-center gap-3 p-2 rounded-lg border-l-2 ${status.connected ? 'bg-white/5' : 'opacity-40'}`}
                  style={{ borderLeftColor: CHANNEL_COLORS[ch] }}>
                  <Icon size={16} style={{ color: CHANNEL_COLORS[ch] }} />
                  <span className="text-sm flex-1 capitalize">{ch}</span>
                  <div className={`w-2 h-2 rounded-full ${status.connected ? 'bg-emerald-400' : 'bg-gray-600'}`} />
                </div>
              );
            })}
            {data?.connected_channels === 0 && (
              <div className="text-center py-4">
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">{t('dashboard.noChannels')}</p>
                <Button variant="outline" size="sm" onClick={() => navigate('/settings')} className="border-white/10">
                  {t('dashboard.connectChannels')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Posts */}
        <Card className="md:col-span-5 card-sheen rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText size={16} className="text-[hsl(var(--primary))]" />
              {t('dashboard.recentPosts')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(data?.recent_posts || []).length === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-4">{t('activity.noActivity')}</p>
            ) : (
              (data?.recent_posts || []).map((post, i) => (
                <div key={i} className="flex items-start gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{post.content?.substring(0, 60)}...</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className={`text-[10px] ${
                        post.status === 'published' ? 'border-emerald-500/30 text-emerald-300' :
                        post.status === 'scheduled' ? 'border-sky-500/30 text-sky-300' :
                        'border-white/20 text-gray-400'
                      }`}>
                        {post.status}
                      </Badge>
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">
                        {new Date(post.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Upcoming Scheduled */}
        <Card className="md:col-span-3 card-sheen rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock size={16} className="text-sky-400" />
              {t('dashboard.upcoming')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(data?.upcoming_posts || []).length === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-4">{t('calendar.noEvents')}</p>
            ) : (
              (data?.upcoming_posts || []).map((post, i) => (
                <div key={i} className="p-2 rounded-lg border border-white/5 bg-white/5">
                  <p className="text-xs truncate">{post.content?.substring(0, 40)}</p>
                  <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                    {post.scheduled_at ? new Date(post.scheduled_at).toLocaleString() : ''}
                  </p>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="md:col-span-12 card-sheen rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp size={16} className="text-[hsl(var(--accent))]" />
                {t('dashboard.activity')}
              </CardTitle>
              <Link to="/activity" className="text-xs text-[hsl(var(--primary))] hover:underline flex items-center gap-1">
                {t('common.next')} <ArrowRight size={12} />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data?.recent_activity || []).slice(0, 5).map((act, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors">
                  <div className="w-2 h-2 rounded-full bg-[hsl(var(--accent))]" />
                  <span className="text-sm flex-1">{t(`activity.${act.action}`, act.action)}</span>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">
                    {new Date(act.created_at).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
